import Controller from '@ember/controller';
import { action } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { Disclaimer, formatMoney } from '@repo/design-system-kit';
import { Link } from '@repo/poly-hooks/components';
import {
  mapPricePlanProps,
  PricePlanContainerSkeleton,
  PricePlansContainerBridge,
  ToggleButtonRecurrence,
} from '@repo/pricing-kit/components';
import { dropTask } from 'ember-concurrency';

import { BackButton } from 'register-partners-js/react/components/back-button';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { ErrorInfo } from 'register-partners-js/utils/error-info';

import styles from './styles.module.css';

const YEARLY = 'yearly';

export default class RegistrationPricePlanController extends Controller {
  mainLayout = MainLayout;

  toggleButtonRecurrence = ToggleButtonRecurrence;
  pricePlansContainer = PricePlansContainerBridge;
  pricePlansContainerSkeleton = PricePlanContainerSkeleton;
  disclaimerBlock = Disclaimer.Block;

  @service router;
  @service segment;
  @service intl;
  @service checkout;
  @service toastFlashMessages;
  @service sentry;

  @alias('model.registration') registration;
  @alias('model.pricePlans') pricePlans;

  queryParams = ['period'];

  @tracked period = YEARLY;
  @tracked banner = null;

  @action
  selectPeriod(period) {
    this.period = period;
    this.segment.track('onboarding_recurrence_toggle_clicked', {
      recurrence: this.recurrence,
    });
  }

  @action
  handleGoBack() {
    this.router.transitionTo('react', `registrations/${this.registration.id}/owner-info`);
  }

  get isPeriodAnnual() {
    return this.period === YEARLY;
  }

  get recurrence() {
    return this.isPeriodAnnual ? 'annual' : 'monthly';
  }

  async onPricePlanPressCta(code, allPricePlans) {
    try {
      this.selectedPlan = code;
      await this.savePricePlansTask.perform(allPricePlans.find(p => p.code === code));

      this.segment.track('onboarding_partner_plan_chosen', {
        original_price_plan_code: code,
        recurrence: this.recurrence,
      });

      this.router.transitionTo('registration.payment');
    } catch (error) {
      if (ErrorInfo.for(error).shouldSendToSentry) {
        this.sentry.captureException(error);
      }
      this.toastFlashMessages.toastError(this.intl.t('errors.internal-server-error'));
    } finally {
      this.selectedPlan = undefined;
    }
  }

  get hasPromoOffers() {
    let allPricePlans = this.getPricePlansTask.lastSuccessful?.value;
    if (allPricePlans) {
      return allPricePlans.some(plan => {
        return plan.monthly_price.promo_offer || plan.annual_price.promo_offer;
      });
    }
  }

  get promoBanner() {
    if (this.hasPromoOffers) {
      return this.banner;
    }
  }

  get pricePlansMapped() {
    let allPricePlans = this.getPricePlansTask.lastSuccessful?.value;

    let { pricePlans } = mapPricePlanProps(allPricePlans, {
      isCompanyCreation: true,
      country: this.registration.organization.get('legalCountry'),
      isSubmitting: this.savePricePlansTask.isRunning,
      mainButton: {
        text: this.intl.t('generic.choose.cta'),
        onPress: code => this.onPricePlanPressCta(code, allPricePlans),
      },
      addOnsTitle: this.intl.t('price_plan.add-ons.title'),
      promoOfferBadge: this.intl.t('badges.limited-time'),
      selectedPlan: this.selectedPlan,
      recurrence: this.recurrence,
      formatPrice: amount =>
        formatMoney({
          amount,
          locale: this.intl.primaryLocale,
          token: 'money-symbol-compact',
        }),
      descriptionCta: {
        type: 'button',
        onPress: () => {
          this.selectPeriod(YEARLY);
        },
      },
      hidePromoInAllProducts: false,
      hideOfferDetails: true,
    });

    return {
      pricePlans,
      hidePromoInAllProducts: !this.hasPromoOffers,
      hasPromoInAnyProducts: this.hasPromoOffers,
    };
  }

  getPricePlansTask = dropTask(async ({ legalCountry, partnerName }) => {
    let { products, banner } = await this.checkout.fetchPricePlansForCompanyCreationTask.perform({
      legalCountry,
      partnerName,
    });
    this.banner = banner;

    return products;
  });

  savePricePlansTask = dropTask(async plan => {
    await this.saveOldPlan(plan.code);
    await this.checkout.savePricePlan({
      organizationId: this.registration.organization.get('id'),
      planId: plan.id,
      planCode: plan.code,
      recurrence: this.recurrence,
      flow: 'company_creation',
      legalCountry: this.registration.organization.get('legalCountry'),
    });
  });

  confirmPlanTask = dropTask(async pricePlanCode => {
    await this.saveOldPlan(pricePlanCode);

    this.segment.track('onboarding_partner_plan_chosen', {
      original_price_plan_code: pricePlanCode,
      recurrence: this.recurrence,
    });

    await this.router.transitionTo('registration.payment');
  });

  async saveOldPlan(pricePlanCode) {
    this.registration.set('pricePlanCode', pricePlanCode);
    this.registration.originalRecurrence = this.recurrence;

    await this.registration.save();
  }
  styles = styles;
  Link = Link;
  BackButton = BackButton;
}
